import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import axios from '../../lib/axios'
import rawAxios from 'axios'
import { Mail, Plus, Trash2, CheckCircle, AlertCircle, RefreshCw, MoreVertical, Zap, Settings2, TrendingUp } from 'lucide-react'
import '../Pages.css'

export default function EmailAccountsList() {
  const [accounts, setAccounts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [deleteLoading, setDeleteLoading] = useState(null)
  const [warmupLoading, setWarmupLoading] = useState(null)

  useEffect(() => {
    fetchAccounts()
  }, [])

  const smtpBaseUrl = import.meta.env.VITE_SMTP_URL

  const getSmtpHeaders = () => {
    const token = localStorage.getItem('access_token')
    return token ? { Authorization: `Bearer ${token}` } : {}
  }

  const fetchAccounts = async () => {
    setLoading(true)
    setError('')
    try {
      const res = await axios.get('email/accounts')
      const accountsList = res.data?.accounts || res.data || []

      // Fetch warmup status for each account from SMTP server
      const accountsWithWarmup = await Promise.all(
        accountsList.map(async (account) => {
          try {
            const statusRes = await rawAxios.get(
              `${smtpBaseUrl}api/warmup/status/${account.id}`,
              { headers: getSmtpHeaders() }
            )
            const warmupData = statusRes.data?.data || statusRes.data || {}
            return {
              ...account,
              warmup_enabled: warmupData.is_enabled || false,
              warmup_status: warmupData.status,
              warmup_progress: warmupData.progress_percent,
              warmup_daily_limit: warmupData.daily_limits?.send,
            }
          } catch {
            return account
          }
        })
      )

      setAccounts(accountsWithWarmup)
    } catch (err) {
      console.error('Failed to fetch email accounts:', err)
      setError(err.response?.data?.detail || err.message || 'Failed to load email accounts')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (accountId) => {
    if (!confirm('Are you sure you want to delete this email account?')) return

    setDeleteLoading(accountId)
    try {
      await axios.delete(`email/accounts/${accountId}`)
      setAccounts(accounts.filter(acc => acc.id !== accountId))
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete account')
    } finally {
      setDeleteLoading(null)
    }
  }

  const toggleWarmup = async (accountId, currentStatus) => {
    setWarmupLoading(accountId)
    try {
      const endpoint = currentStatus ? 'disable' : 'enable'
      const user = JSON.parse(localStorage.getItem('synxreach_user') || '{}')
      const params = endpoint === 'enable' ? { user_id: user.id } : {}
      const res = await rawAxios.post(
        `${smtpBaseUrl}api/warmup/${endpoint}/${accountId}`,
        {},
        { headers: getSmtpHeaders(), params }
      )

      // Update the account in the list
      setAccounts(accounts.map(acc =>
        acc.id === accountId
          ? { ...acc, warmup_enabled: !currentStatus, warmup_status: res.data?.data?.warmup_status }
          : acc
      ))
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to toggle warmup')
    } finally {
      setWarmupLoading(null)
    }
  }

  const getProviderIcon = (email, provider) => {
    const domain = email?.split('@')[1]?.toLowerCase() || ''

    if (domain.includes('gmail') || domain.includes('google')) {
      return (
        <div style={{
          width: '48px',
          height: '48px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #4285F4 0%, #34A853 100%)',
          borderRadius: 'var(--radius-md)',
          fontSize: '1.25rem',
          fontWeight: '600',
          color: 'white'
        }}>
          G
        </div>
      )
    }

    return (
      <div style={{
        width: '48px',
        height: '48px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
        borderRadius: 'var(--radius-md)',
        color: 'white'
      }}>
        <Mail size={24} />
      </div>
    )
  }

  return (
    <div className="page animate-fade-in">
      <div className="page-header">
        <div className="header-info">
          <h2>Email Accounts</h2>
          <p>Manage your connected email accounts and warmup settings</p>
          {!loading && accounts.length > 0 && (
            <div style={{
              marginTop: '0.75rem',
              display: 'flex',
              gap: '1.5rem',
              fontSize: '0.85rem'
            }}>
              <span style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                color: 'var(--color-text-secondary)'
              }}>
                <div style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: '#22c55e'
                }} />
                {accounts.filter(a => a.is_active !== false).length} Active
              </span>
              <span style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                color: 'var(--color-text-secondary)'
              }}>
                <Zap size={14} style={{ color: '#6366f1' }} />
                {accounts.filter(a => a.warmup_enabled).length} Warming Up
              </span>
            </div>
          )}
        </div>
        <Link to="/email-accounts/add" className="btn btn-primary" style={{
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
          boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)'
        }}>
          <Plus size={18} />
          <span>Add New Account</span>
        </Link>
      </div>

      {error && (
        <div className="card" style={{
          padding: '1.5rem',
          background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.05) 0%, rgba(220, 38, 38, 0.05) 100%)',
          border: '1px solid rgba(239, 68, 68, 0.2)',
          marginBottom: '1.5rem'
        }}>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
            <div style={{
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(239, 68, 68, 0.1)',
              borderRadius: 'var(--radius-md)',
              flexShrink: 0
            }}>
              <AlertCircle size={20} style={{ color: '#ef4444' }} />
            </div>
            <div style={{ flex: 1 }}>
              <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.95rem', fontWeight: '600', color: '#dc2626' }}>
                Failed to Load Accounts
              </h4>
              <p style={{ margin: 0, color: '#dc2626', fontSize: '0.85rem', lineHeight: '1.5' }}>
                {error}
              </p>
            </div>
            <button
              onClick={fetchAccounts}
              className="btn btn-primary"
              style={{ padding: '0.5rem 1rem', flexShrink: 0 }}
            >
              <RefreshCw size={16} />
              <span>Retry</span>
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px'
        }}>
          <div className="loader"></div>
        </div>
      ) : accounts.length === 0 ? (
        <div className="card" style={{
          maxWidth: '700px',
          margin: '3rem auto',
          background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.03) 0%, rgba(139, 92, 246, 0.03) 100%)',
          border: '1px solid rgba(99, 102, 241, 0.1)'
        }}>
          <div style={{
            padding: '5rem 2rem',
            textAlign: 'center'
          }}>
            <div style={{
              width: '120px',
              height: '120px',
              margin: '0 auto 2rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              borderRadius: '50%',
              boxShadow: '0 20px 40px rgba(99, 102, 241, 0.3)'
            }}>
              <Mail size={56} style={{ color: 'white' }} />
            </div>
            <h3 style={{
              marginBottom: '0.75rem',
              fontSize: '1.75rem',
              fontWeight: '700',
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              No Email Accounts Connected
            </h3>
            <p style={{
              color: 'var(--color-text-secondary)',
              marginBottom: '2.5rem',
              fontSize: '1rem',
              lineHeight: '1.6',
              maxWidth: '500px',
              margin: '0 auto 2.5rem'
            }}>
              Connect your email account to start sending campaigns, tracking responses, and growing your business with intelligent email outreach.
            </p>
            <Link to="/email-accounts/add" className="btn btn-primary" style={{
              padding: '0.875rem 2rem',
              fontSize: '1rem',
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)'
            }}>
              <Plus size={20} />
              <span>Connect Your First Account</span>
            </Link>

            <div style={{
              marginTop: '3rem',
              padding: '1.5rem',
              background: 'var(--color-bg-secondary)',
              borderRadius: 'var(--radius-lg)',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '1.5rem',
              textAlign: 'left'
            }}>
              <div>
                <div style={{
                  width: '40px',
                  height: '40px',
                  background: 'rgba(99, 102, 241, 0.1)',
                  borderRadius: 'var(--radius-md)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '0.75rem'
                }}>
                  <Zap size={20} style={{ color: '#6366f1' }} />
                </div>
                <h4 style={{ margin: '0 0 0.25rem 0', fontSize: '0.875rem', fontWeight: '600' }}>
                  Auto Warmup
                </h4>
                <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--color-text-muted)', lineHeight: '1.4' }}>
                  Automatically warm up your email account for better deliverability
                </p>
              </div>
              <div>
                <div style={{
                  width: '40px',
                  height: '40px',
                  background: 'rgba(99, 102, 241, 0.1)',
                  borderRadius: 'var(--radius-md)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '0.75rem'
                }}>
                  <TrendingUp size={20} style={{ color: '#6366f1' }} />
                </div>
                <h4 style={{ margin: '0 0 0.25rem 0', fontSize: '0.875rem', fontWeight: '600' }}>
                  Track Performance
                </h4>
                <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--color-text-muted)', lineHeight: '1.4' }}>
                  Monitor sent and received emails with detailed analytics
                </p>
              </div>
              <div>
                <div style={{
                  width: '40px',
                  height: '40px',
                  background: 'rgba(99, 102, 241, 0.1)',
                  borderRadius: 'var(--radius-md)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '0.75rem'
                }}>
                  <CheckCircle size={20} style={{ color: '#6366f1' }} />
                </div>
                <h4 style={{ margin: '0 0 0.25rem 0', fontSize: '0.875rem', fontWeight: '600' }}>
                  Easy Setup
                </h4>
                <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--color-text-muted)', lineHeight: '1.4' }}>
                  Connect Gmail, Outlook, or any email provider in minutes
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gap: '1.25rem',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 450px), 1fr))'
        }}>
          {accounts.map((account) => (
            <div key={account.id} className="card" style={{
              transition: 'all 0.2s',
              position: 'relative',
              overflow: 'hidden'
            }}>
              {/* Header Section */}
              <div style={{
                padding: '1.5rem',
                borderBottom: '1px solid var(--color-border)'
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                  {getProviderIcon(account.email, account.provider)}

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                      <h3 style={{
                        fontSize: '1.05rem',
                        fontWeight: '600',
                        margin: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '100%'
                      }}>
                        {account.email}
                      </h3>
                      {account.is_active !== false && (
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                          padding: '0.25rem 0.5rem',
                          background: 'rgba(34, 197, 94, 0.1)',
                          color: '#16a34a',
                          borderRadius: 'var(--radius-sm)',
                          fontSize: '0.7rem',
                          fontWeight: '600',
                          textTransform: 'uppercase',
                          letterSpacing: '0.025em'
                        }}>
                          <CheckCircle size={11} />
                          Active
                        </span>
                      )}
                    </div>

                    {account.first_name && account.last_name && (
                      <div style={{
                        fontSize: '0.9rem',
                        color: 'var(--color-text-secondary)',
                        marginBottom: '0.5rem'
                      }}>
                        {account.first_name} {account.last_name}
                      </div>
                    )}

                    {account.provider && (
                      <span style={{
                        display: 'inline-block',
                        padding: '0.25rem 0.625rem',
                        background: 'var(--color-bg-secondary)',
                        borderRadius: 'var(--radius-sm)',
                        fontSize: '0.75rem',
                        fontWeight: '500',
                        color: 'var(--color-text-secondary)'
                      }}>
                        {account.provider === 'imap' ? 'IMAP/SMTP' : account.provider.toUpperCase()}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Warmup Section */}
              <div style={{
                padding: '1rem 1.5rem',
                background: account.warmup_enabled
                  ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(139, 92, 246, 0.05) 100%)'
                  : 'var(--color-bg-secondary)',
                borderBottom: '1px solid var(--color-border)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{
                      width: '36px',
                      height: '36px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: account.warmup_enabled
                        ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
                        : 'var(--color-bg-tertiary)',
                      borderRadius: 'var(--radius-md)',
                      color: account.warmup_enabled ? 'white' : 'var(--color-text-muted)'
                    }}>
                      <Zap size={18} />
                    </div>
                    <div>
                      <div style={{
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        color: 'var(--color-text-primary)',
                        marginBottom: '0.125rem'
                      }}>
                        Email Warmup
                      </div>
                      <div style={{
                        fontSize: '0.75rem',
                        color: 'var(--color-text-muted)'
                      }}>
                        {account.warmup_enabled
                          ? `${account.warmup_daily_limit || 20} emails/day`
                          : 'Not warming up'}
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => toggleWarmup(account.id, account.warmup_enabled)}
                    disabled={warmupLoading === account.id}
                    className="btn"
                    style={{
                      padding: '0.5rem 1rem',
                      fontSize: '0.8rem',
                      background: account.warmup_enabled
                        ? 'transparent'
                        : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                      color: account.warmup_enabled ? 'var(--color-text-secondary)' : 'white',
                      border: account.warmup_enabled ? '1px solid var(--color-border)' : 'none'
                    }}
                  >
                    {warmupLoading === account.id ? (
                      <RefreshCw size={14} className="animate-spin" />
                    ) : (
                      <>
                        <span>{account.warmup_enabled ? 'Disable' : 'Enable'}</span>
                      </>
                    )}
                  </button>
                </div>

                {/* Warmup Progress */}
                {account.warmup_enabled && account.warmup_progress !== undefined && (
                  <div style={{ marginTop: '0.75rem' }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '0.375rem'
                    }}>
                      <span style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)' }}>
                        Warmup Progress
                      </span>
                      <span style={{ fontSize: '0.7rem', fontWeight: '600', color: '#6366f1' }}>
                        {account.warmup_progress || 0}%
                      </span>
                    </div>
                    <div style={{
                      height: '4px',
                      background: 'rgba(0, 0, 0, 0.1)',
                      borderRadius: '2px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        height: '100%',
                        background: 'linear-gradient(90deg, #6366f1 0%, #8b5cf6 100%)',
                        width: `${account.warmup_progress || 0}%`,
                        transition: 'width 0.3s ease'
                      }} />
                    </div>
                  </div>
                )}
              </div>

              {/* Stats Section */}
              {(account.emails_sent !== undefined || account.emails_received !== undefined) && (
                <div style={{
                  padding: '1rem 1.5rem',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '1rem',
                  borderBottom: '1px solid var(--color-border)'
                }}>
                  <div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Sent
                    </div>
                    <div style={{ fontSize: '1.25rem', fontWeight: '700', color: 'var(--color-text-primary)' }}>
                      {account.emails_sent || 0}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Received
                    </div>
                    <div style={{ fontSize: '1.25rem', fontWeight: '700', color: 'var(--color-text-primary)' }}>
                      {account.emails_received || 0}
                    </div>
                  </div>
                </div>
              )}

              {/* Server Details */}
              {account.imap_host && (
                <div style={{
                  padding: '1rem 1.5rem',
                  background: 'var(--color-bg-secondary)',
                  fontSize: '0.75rem',
                  color: 'var(--color-text-muted)',
                  fontFamily: 'monospace',
                  borderBottom: '1px solid var(--color-border)'
                }}>
                  <div style={{ marginBottom: '0.25rem' }}>
                    IMAP: {account.imap_host}:{account.imap_port || 993}
                  </div>
                  <div>
                    SMTP: {account.smtp_host}:{account.smtp_port || 587}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div style={{
                padding: '1rem 1.5rem',
                display: 'flex',
                gap: '0.5rem',
                justifyContent: 'flex-end'
              }}>
                <button
                  className="btn btn-ghost"
                  style={{
                    padding: '0.5rem 1rem',
                    fontSize: '0.85rem'
                  }}
                >
                  <Settings2 size={16} />
                  <span>Settings</span>
                </button>
                <button
                  onClick={() => handleDelete(account.id)}
                  disabled={deleteLoading === account.id}
                  className="btn btn-ghost"
                  style={{
                    padding: '0.5rem 1rem',
                    fontSize: '0.85rem',
                    color: '#ef4444'
                  }}
                >
                  {deleteLoading === account.id ? (
                    <RefreshCw size={16} className="animate-spin" />
                  ) : (
                    <Trash2 size={16} />
                  )}
                  <span>Delete</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
